import { useEffect } from "react";
import { ImageOverlay, Pane, useMap } from "react-leaflet";
import styles from "./RadarPlaybackLayer.module.css";
import {
  RAIN_RADAR_PANE,
  RAIN_RADAR_PANE_Z_INDEX,
} from "./radarPlaybackShared";
import { useFmMapObservation } from "@/components/ui/FmMapObservationContext";
import type { FmMapRadarFrameDescriptor } from "@/components/ui/FmMapWorkspaceRoot.types";

// The radar frames are the FMI *national* composite (one image spanning all of
// Finland). Painted raw at low zoom it washes the whole country and — layered on
// the dark basemap — collapses into an opaque mass over the property
// We clip the radar pane to a regional window around the
// selected property so it reads as "storm cells crossing your forest", never a
// national bleed. Padding is geographic (WGS84 degrees), min-clamped so a tiny
// property still gets a sensible window; radar georeferencing is untouched.
const RADAR_CLIP_PAD_FACTOR = 1.5;
const RADAR_CLIP_MIN_PAD_LNG_DEG = 0.3; // ~16 km E–W at ~61°N
const RADAR_CLIP_MIN_PAD_LAT_DEG = 0.18; // ~20 km N–S

/**
 * Clip the rain-radar pane to a regional window around the property so the
 * national composite never bleeds across the whole map. Recomputes on every
 * view change (layer-point space stays aligned with the ImageOverlay, which
 * Leaflet also positions in layer points). No-op (clip cleared) without a bbox.
 */
function useRadarPaneClip(propertyBbox?: readonly number[] | null): void {
  const map = useMap();
  useEffect(() => {
    const pane = map.getPane(RAIN_RADAR_PANE);
    if (!pane) return undefined;
    pane.dataset.fmMapLayer = "rain-radar";
    if (
      !propertyBbox ||
      propertyBbox.length !== 4 ||
      propertyBbox.some((value) => !Number.isFinite(value))
    ) {
      pane.style.clipPath = "";
      return undefined;
    }
    const [minLng, minLat, maxLng, maxLat] = propertyBbox;
    const padLng = Math.max(
      (maxLng - minLng) * RADAR_CLIP_PAD_FACTOR,
      RADAR_CLIP_MIN_PAD_LNG_DEG,
    );
    const padLat = Math.max(
      (maxLat - minLat) * RADAR_CLIP_PAD_FACTOR,
      RADAR_CLIP_MIN_PAD_LAT_DEG,
    );
    const west = minLng - padLng;
    const east = maxLng + padLng;
    const south = minLat - padLat;
    const north = maxLat + padLat;

    const update = () => {
      const nw = map.latLngToLayerPoint([north, west]);
      const se = map.latLngToLayerPoint([south, east]);
      pane.style.clipPath =
        `polygon(${nw.x}px ${nw.y}px, ${se.x}px ${nw.y}px, ` +
        `${se.x}px ${se.y}px, ${nw.x}px ${se.y}px)`;
    };

    update();
    map.on("zoomend viewreset resize moveend", update);
    return () => {
      map.off("zoomend viewreset resize moveend", update);
      pane.style.clipPath = "";
    };
  }, [map, propertyBbox]);
}

interface RadarPlaybackLayerProps {
  propertyBbox?: [number, number, number, number] | null;
}

type LatLngBoundsTuple = [[number, number], [number, number]];

function isWgs84Bbox(
  bbox: readonly [number, number, number, number],
): boolean {
  const [minLng, minLat, maxLng, maxLat] = bbox;
  return (
    Math.abs(minLng) <= 180 &&
    Math.abs(maxLng) <= 180 &&
    Math.abs(minLat) <= 90 &&
    Math.abs(maxLat) <= 90
  );
}

function epsg3067ToWgs84(x: number, y: number): [number, number] {
  const a = 6378137.0;
  const f = 1 / 298.257222101;
  const lat0 = 0;
  const lon0 = (27 * Math.PI) / 180;
  const k0 = 0.9996;
  const falseEasting = 500000.0;
  const falseNorthing = 0.0;
  const e2 = 2 * f - f * f;
  const e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2));

  const m = (y - falseNorthing) / k0;
  const mu =
    m / (a * (1 - e2 / 4 - (3 * e2 * e2) / 64 - (5 * e2 * e2 * e2) / 256));

  const j1 = (3 * e1) / 2 - (27 * e1 ** 3) / 32;
  const j2 = (21 * e1 * e1) / 16 - (55 * e1 ** 4) / 32;
  const j3 = (151 * e1 ** 3) / 96;
  const j4 = (1097 * e1 ** 4) / 512;
  const fp =
    mu +
    j1 * Math.sin(2 * mu) +
    j2 * Math.sin(4 * mu) +
    j3 * Math.sin(6 * mu) +
    j4 * Math.sin(8 * mu);

  const sinFp = Math.sin(fp);
  const cosFp = Math.cos(fp);
  const tanFp = Math.tan(fp);
  const c1 = (e2 / (1 - e2)) * cosFp * cosFp;
  const t1 = tanFp * tanFp;
  const n1 = a / Math.sqrt(1 - e2 * sinFp * sinFp);
  const r1 = (a * (1 - e2)) / (1 - e2 * sinFp * sinFp) ** 1.5;
  const d = (x - falseEasting) / (n1 * k0);

  const lat =
    fp -
    ((n1 * tanFp) / r1) *
      ((d * d) / 2 -
        ((5 + 3 * t1 + 10 * c1 - 4 * c1 * c1 - 9 * (e2 / (1 - e2))) * d ** 4) /
          24 +
        ((61 +
          90 * t1 +
          298 * c1 +
          45 * t1 * t1 -
          252 * (e2 / (1 - e2)) -
          3 * c1 * c1) *
          d ** 6) /
          720);
  const lon =
    lon0 +
    (d -
      ((1 + 2 * t1 + c1) * d ** 3) / 6 +
      ((5 -
        2 * c1 +
        28 * t1 -
        3 * c1 * c1 +
        8 * (e2 / (1 - e2)) +
        24 * t1 * t1) *
        d ** 5) /
        120) /
      cosFp;

  return [(lat + lat0) * (180 / Math.PI), lon * (180 / Math.PI)];
}

function frameBounds(frame: FmMapRadarFrameDescriptor): LatLngBoundsTuple | null {
  const { bbox, crs } = frame.grid;
  const [left, bottom, right, top] = bbox;
  if (crs === "EPSG:4326" || isWgs84Bbox(bbox)) {
    return [
      [bottom, left],
      [top, right],
    ];
  }
  if (crs === "EPSG:3067") {
    const corners = [
      epsg3067ToWgs84(left, bottom),
      epsg3067ToWgs84(left, top),
      epsg3067ToWgs84(right, bottom),
      epsg3067ToWgs84(right, top),
    ];
    const lats = corners.map(([lat]) => lat);
    const lngs = corners.map(([, lng]) => lng);
    return [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    ];
  }
  return null;
}

interface FrameState {
  activeFrame: FmMapRadarFrameDescriptor | null;
  activeBounds: LatLngBoundsTuple | null;
  renderReady: boolean;
  layerStatus: string | null;
}

/** Derive render-readiness + unavailable-reason for the active radar frame. */
function deriveFrameState(
  frames: readonly FmMapRadarFrameDescriptor[],
  activeIndex: number,
): FrameState {
  const activeFrame = frames[activeIndex] ?? null;
  const activeBounds = activeFrame ? frameBounds(activeFrame) : null;
  const renderReady =
    activeFrame?.render.status === "ready" &&
    Boolean(activeFrame.render.imageUrl) &&
    activeBounds !== null;
  const layerStatus =
    activeFrame?.render.status === "unavailable"
      ? (activeFrame.render.reason?.replace(/_/g, " ") ?? null)
      : null;
  return { activeFrame, activeBounds, renderReady, layerStatus };
}

function RadarFrameOverlay({
  activeBounds,
  activeFrame,
  opacity,
  renderReady,
}: {
  activeBounds: LatLngBoundsTuple | null;
  activeFrame: FmMapRadarFrameDescriptor | null;
  opacity: number;
  renderReady: boolean;
}) {
  const imageUrl =
    activeFrame?.render.status === "ready" ? activeFrame.render.imageUrl : null;
  if (!renderReady || !activeBounds || !imageUrl) return null;
  return (
    <ImageOverlay
      url={imageUrl}
      bounds={activeBounds}
      opacity={opacity / 100}
      interactive={false}
      className="fm-rain-radar-image"
    />
  );
}

const RadarPlaybackLayer: React.FC<RadarPlaybackLayerProps> = ({
  propertyBbox,
}) => {
  const model = useFmMapObservation();

  useRadarPaneClip(propertyBbox);
  const { activeBounds, renderReady } = deriveFrameState(
    model.activeFrame ? [model.activeFrame] : [],
    0,
  );

  return (
    <Pane
      name={RAIN_RADAR_PANE}
      className={styles.radarPane}
      style={{ zIndex: RAIN_RADAR_PANE_Z_INDEX, pointerEvents: "none" }}
    >
      {model.enabled ? (
        <RadarFrameOverlay
          activeBounds={activeBounds}
          activeFrame={model.activeFrame}
          opacity={model.opacity}
          renderReady={renderReady}
        />
      ) : null}
    </Pane>
  );
};

export default RadarPlaybackLayer;
